import _asyncToGenerator from "/home/runner/work/mainrepo/mainrepo/node_modules/.pnpm/@babel+runtime@7.29.2/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
var AccessState_1;
import { __decorate, __metadata } from "tslib";
import { isPlatformServer } from '@angular/common';
import { DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { simpleAbilityCheck } from '@core/shared/ability';
import { AccessServiceDataAccessor, AccessServicePlugin, OnSessionEnd, OnSessionInitialized, OnSessionReloaded, roleCheck, SessionAccessInfoAndData, SessionUtilsShared, UserRole } from '@core/shared/access';
import { arrayUnique } from '@core/shared/utils';
import { Action, createSelector, NgxsSimpleChange, Selector, State, StateContext, Store } from '@ngxs/store';
import { filter, from, map, Observable, of, take } from 'rxjs';
import { PageState } from '../../page-state/page.state';
import { PersistStateFactory, takeWhileAlive } from '../../utils';
import { AUTHCODE_ID_KEY, LOCALSTORAGE_SESSIONTOKEN_KEY, STORAGE_KEY_REFERRER_CODE, URL_PARAM_REFERRER_CODE } from '../constants';
import { AccessActions } from './access.actions';
import { AccessService } from './access.service';
import * as i0 from "@angular/core";
import * as i1 from "./access.service";
import * as i2 from "../../utils";
import * as i3 from "@ngxs/store";
let AccessState = /*#__PURE__*/(() => {
  let AccessState = class AccessState {
    static {
      AccessState_1 = this;
    }
    // general --------------------------------------------------------------------------
    static accessData(name, extraParam = null, handleUnknown = 'null') {
      return createSelector([AccessState_1.sessionInfo], sessionInfo => {
        const accessorInfo = this._findDataAccessor(name, handleUnknown);
        if (!accessorInfo) return null;
        return this._executeDataAccessor(accessorInfo.plugin, accessorInfo.accessor, sessionInfo, extraParam);
      });
    }
    static sessionId(state) {
      return state.sessionId || null;
    }
    static sessionInfo(state) {
      return state.sessionInfo;
    }
    static referrerCode(state) {
      return state.referrerCode || null;
    }
    static authCodes(state) {
      return state.authCodes || [];
    }
    static loggedIn(state) {
      return Boolean(state?.sessionInfo?.sessionState && SessionUtilsShared.hasAuthInfo(state.sessionInfo.sessionState));
    }
    static isReady(state) {
      return state.isReady || state.suppressIsReady;
    }
    // access rights ----------------------------------------------------------------------
    static isAdmin(state) {
      return roleCheck('ADMINISTRATOR', state.sessionInfo?.role || UserRole.GUEST);
    }
    static isSuperUser(state) {
      return roleCheck('SUPERUSER', state.sessionInfo?.role || UserRole.GUEST);
    }
    static role(state) {
      return state.sessionInfo?.role || UserRole.GUEST;
    }
    static activatedFeatures(state) {
      return state.sessionInfo?.activatedFeatures || [];
    }
    static abilities(state) {
      return state.sessionInfo?.abilities || {};
    }
    static simpleAbilityCheck(requirement) {
      return createSelector([AccessState_1], state => {
        const grantedRole = state.sessionInfo?.role || UserRole.GUEST;
        const grantedAbilities = state.sessionInfo?.abilities || [];
        return simpleAbilityCheck(grantedRole, grantedAbilities, requirement);
      });
    }
    static hasRole(role) {
      return createSelector([AccessState_1], state => {
        const grantedRole = state.sessionInfo?.role || UserRole.GUEST;
        return roleCheck(role, grantedRole);
      });
    }
    static hasAuthCode(authCode) {
      return createSelector([AccessState_1], state => {
        const p = state.authCodes || [];
        return p.indexOf(authCode) >= 0;
      });
    }
    // payload / user data --------------------------------------------------------------
    static sessionData(state) {
      return state.sessionInfo?.sessionState.data || {};
    }
    constructor(accessService, persistenceFactory, store, ngPlatformId) {
      this.accessService = accessService;
      this.persistenceFactory = persistenceFactory;
      this.store = store;
      this.ngPlatformId = ngPlatformId;
      this.destroyRef = inject(DestroyRef);
      this.stateInitialized = false;
      this.persistedSessionToken = this.persistenceFactory.create(LOCALSTORAGE_SESSIONTOKEN_KEY, false, 'local');
      this.persistedAuthCode = this.persistenceFactory.create(AUTHCODE_ID_KEY, false, 'local');
      this.persistedReferrerCode = this.persistenceFactory.create(STORAGE_KEY_REFERRER_CODE, false, 'local');
      AccessState_1._findDataAccessor = (name, handleUnknown = 'null') => {
        return accessService.findDataAccessor(name, handleUnknown);
      };
      AccessState_1._executeDataAccessor = accessService.executeDataAccessor.bind(accessService);
    }
    ngxsOnInit(ctx) {
      if (isPlatformServer(this.ngPlatformId)) {
        // no login state when running SSR!
        ctx.patchState({
          isReady: true,
          sessionId: null
        });
      } else {
        this.bindAccessServiceEvents(ctx);
        this.discoverUrlParams(ctx);
        ctx.dispatch(new AccessActions.StartSession());
        this.stateInitialized = true;
      }
    }
    ngxsOnChanges(change) {
      // ngxsOnChanges is called once BEFORE ngxsOnInit, when changing to EMPTY state!
      // so we need to check if state is actually ready and should persist
      if (this.stateInitialized) {
        this.persistSessionId(change.currentValue.sessionId);
      }
    }
    bindAccessServiceEvents(ctx) {
      this.accessService.events.pipe(filter(event => event instanceof OnSessionInitialized), takeWhileAlive(this.destroyRef)).subscribe(({
        sessionInfo
      }) => {
        const sessionId = sessionInfo ? sessionInfo.sessionState.sessionId : null;
        ctx.patchState({
          isReady: true,
          sessionInfo,
          sessionId
        });
      });
      this.accessService.events.pipe(filter(event => event instanceof OnSessionReloaded), takeWhileAlive(this.destroyRef)).subscribe(({
        sessionInfo
      }) => {
        ctx.patchState({
          sessionInfo: sessionInfo
        });
      });
      this.accessService.events.pipe(filter(event => event instanceof OnSessionEnd), takeWhileAlive(this.destroyRef)).subscribe(event => {
        ctx.patchState({
          isReady: true,
          sessionInfo: null,
          sessionId: null
        });
      });
    }
    // TODO: Should we migrate this to become part of PageState?
    discoverUrlParams(ctx) {
      const params$ = this.store.select(PageState.initialRouteParams);
      params$.pipe(filter(params => params !== null), take(1)).subscribe(params => {
        // console.log('access state discover url params',params)
        // check and store referrerCode
        if (typeof params[URL_PARAM_REFERRER_CODE] === 'string') {
          ctx.dispatch(new AccessActions.SetReferrerCode(params[URL_PARAM_REFERRER_CODE]));
        } else {
          const persistedRefCode = this.persistedReferrerCode.get();
          if (persistedRefCode) ctx.dispatch(new AccessActions.SetReferrerCode(persistedRefCode));
        }
        // check and store authCodes
        const authCodes = [];
        if (typeof params[AUTHCODE_ID_KEY] === 'string') {
          if (Array.isArray(params[AUTHCODE_ID_KEY])) authCodes.push(...params[AUTHCODE_ID_KEY]);else authCodes.push(params[AUTHCODE_ID_KEY]);
        }
        const persistedAuthCode = this.persistedAuthCode.get();
        if (persistedAuthCode) {
          authCodes.push(...persistedAuthCode.split(','));
        }
        if (authCodes.length) {
          ctx.dispatch(new AccessActions.AddAuthCodes(authCodes));
        }
      });
    }
    persistSessionId(sessionId) {
      if (sessionId) this.persistedSessionToken.set(sessionId);else this.persistedSessionToken.remove();
    }
    startSession({
      setState,
      getState,
      patchState
    }) {
      return from(this.accessService.startSession());
    }
    refreshSession({
      setState,
      getState,
      patchState
    }) {
      this.accessService.reloadSession();
    }
    logout({
      setState,
      getState,
      patchState
    }) {
      return of(this.accessService.endSession()).pipe(map(() => {
        setState({
          ...getState(),
          isReady: true,
          sessionId: null,
          sessionInfo: null
        });
        return true;
      }));
    }
    suppressIsReady({
      setState,
      getState,
      patchState
    }) {
      patchState({
        suppressIsReady: true
      });
    }
    setReferrerCode({
      setState,
      getState,
      patchState
    }, {
      referrerCode
    }) {
      patchState({
        referrerCode
      });
      this.persistReferrerCode(referrerCode);
    }
    persistReferrerCode(referrerCode) {
      if (referrerCode) this.persistedReferrerCode.set(referrerCode);else this.persistedReferrerCode.remove();
    }
    addAuthCodes({
      setState,
      getState,
      patchState
    }, {
      authCodes,
      deleteOthers
    }) {
      const existingCodes = deleteOthers ? [] : getState().authCodes;
      let newCodes = [...existingCodes, ...authCodes];
      newCodes = arrayUnique(newCodes);
      patchState({
        authCodes: newCodes
      });
      this.persistedAuthCode.set(newCodes);
    }
    removeAuthCode(_x, _x2) {
      return _asyncToGenerator(function* ({
        setState,
        getState,
        patchState
      }, action) {
        /**
         * Note on requirements: state stores clear-text auth codes.
         * The incoming authCode value to find and remove may be a hash or a clear-text too.
         * We know that a sha-1 hash is 20 bytes, represented as 40 hexadecimal chars.
         */
        // we dont know if the incoming authCode is a hash or not.
        // so we always create the hash and check/remove that as well.
        throw new Error('not working currently, because hashString util was removed from shared and is now backend-only. Reimplement this on server side if needed again.');
        // const existingCodes = getState().authCodes;
        // let filteredCodes = existingCodes.filter(code=>{
        // 	return code!==action.authCode
        // });
        // if(action.authCode.length===40) {
        // 	const hashedCodes = await Promise.all(
        // 		filteredCodes.map(code=>{
        // 			return hashString('sha-1',code);
        // 		})
        // 	)
        // 	filteredCodes = filteredCodes.filter((code,index)=>{
        // 		return hashedCodes[index]!==action.authCode
        // 	});
        // }
        // this.persistedAuthCode.set(filteredCodes)
        // patchState({
        // 	authCodes:filteredCodes
        // })
      }).apply(this, arguments);
    }
    static {
      this.ɵfac = function AccessState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccessState)(i0.ɵɵinject(i1.AccessService), i0.ɵɵinject(i2.PersistStateFactory), i0.ɵɵinject(i3.Store), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: AccessState,
        factory: AccessState.ɵfac
      });
    }
  };
  __decorate([Action(AccessActions.StartSession), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Observable)], AccessState.prototype, "startSession", null);
  __decorate([Action(AccessActions.RefreshSession), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], AccessState.prototype, "refreshSession", null);
  __decorate([Action(AccessActions.Logout), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Observable)], AccessState.prototype, "logout", null);
  __decorate([Action(AccessActions.SuppressIsReady), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], AccessState.prototype, "suppressIsReady", null);
  __decorate([Action(AccessActions.SetReferrerCode), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, AccessActions.SetReferrerCode]), __metadata("design:returntype", void 0)], AccessState.prototype, "setReferrerCode", null);
  __decorate([Action(AccessActions.AddAuthCodes), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, AccessActions.AddAuthCodes]), __metadata("design:returntype", void 0)], AccessState.prototype, "addAuthCodes", null);
  __decorate([Action(AccessActions.RemoveAuthCode), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, AccessActions.RemoveAuthCode]), __metadata("design:returntype", Promise)], AccessState.prototype, "removeAuthCode", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", String)], AccessState, "sessionId", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Object)], AccessState, "sessionInfo", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", String)], AccessState, "referrerCode", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], AccessState, "authCodes", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], AccessState, "loggedIn", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], AccessState, "isReady", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], AccessState, "isAdmin", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], AccessState, "isSuperUser", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", String)], AccessState, "role", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], AccessState, "activatedFeatures", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Object)], AccessState, "abilities", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Object)], AccessState, "sessionData", null);
  return AccessState;
})();
AccessState = AccessState_1 = __decorate([State({
  name: 'access',
  defaults: {
    sessionId: null,
    referrerCode: null,
    authCodes: [],
    sessionInfo: null,
    isReady: false,
    suppressIsReady: false
  }
}), __metadata("design:paramtypes", [AccessService, PersistStateFactory, Store, Object])], AccessState);
export { AccessState };